import type { CityConfig } from "@/pages/city-template/types";

export const oakRidgeConfig: CityConfig = {
  city: "Oak Ridge",
  state: "Tennessee",
  stateAbbr: "TN",
  slug: "oak-ridge",
  route: "/oak-ridge",
  title: "Emergency & Expedited Passport Services Oak Ridge TN | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Oak Ridge, TN — as fast as 24 hours. No Oak Ridge passport agency. Fast Passport Center couriers to the Atlanta Passport Agency at 805 Broadway, about 180 miles west. A+ BBB rated. Serving ORNL scientists, DOE contractors, and Anderson County families.",
  h1: "Emergency & Expedited Passport Services in Oak Ridge, TN",
  heroSubtitle: 'Oak Ridge — the "Secret City" — is home to Oak Ridge National Laboratory (ORNL), the Y-12 National Security Complex, and some of the brightest scientific minds in the world. Fast Passport Center\'s registered couriers deliver your documents to the Atlanta Passport Agency at 805 Broadway so you get a new or renewed passport as fast as 24 hours — whether you\'re an ORNL researcher heading to CERN in Switzerland, a DOE contractor traveling to international facilities, or an Anderson County family planning an overseas vacation.',
  heroImageUrl: "https://readdy.ai/api/search-image?query=Oak%20Ridge%20Tennessee%20with%20the%20American%20Museum%20of%20Science%20and%20Energy%20and%20the%20historic%20Jackson%20Square%20area%20in%20warm%20morning%20light%2C%20classic%20mid-century%20atomic%20city%20architecture%20with%20modern%20scientific%20facilities%20and%20rolling%20East%20Tennessee%20hills%20in%20the%20background%2C%20tree-lined%20streets%20with%20professional%20landscaping%2C%20wide%20angle%20landscape%20photography&width=1800&height=700&seq=oak-ridge-tn-hero-001&orientation=landscape",
  heroImageAlt: "Oak Ridge Tennessee with the American Museum of Science and Energy and East Tennessee hills",
  geoPosition: "36.0104;-84.2696",
  geoPlacename: "Oak Ridge, Tennessee",
  lastModified: "2026-06-06",
  regionalAgency: {
    name: "Atlanta Passport Agency",
    address: "230 Peachtree St NW, Suite 1000, Atlanta, GA 30303",
    distance: "Approximately 180 miles south of Oak Ridge — about 3 hours via I-75",
  },
  nearbyCitiesZips: [
    { name: "Oak Ridge", zips: "37830, 37831" },
    { name: "Clinton", zips: "37716" },
    { name: "Norris", zips: "37828" },
    { name: "Lake City", zips: "37769" },
    { name: "Oliver Springs", zips: "37840" },
    { name: "Kingston", zips: "37763" },
    { name: "Harriman", zips: "37748" },
    { name: "Lenoir City", zips: "37771, 37772" },
    { name: "Farragut", zips: "37934" },
    { name: "Hardin Valley", zips: "37932" },
    { name: "Powell", zips: "37849" },
    { name: "Knoxville", zips: "37902, 37912, 37914, 37915" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Courier delivery to Atlanta Passport Agency — 180 miles from Oak Ridge via I-40" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-map-pin-2-line", title: "East TN Scientific Hub", desc: "Serving Oak Ridge, Clinton, Norris, and all Anderson County" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your Oak Ridge case from start to finish" },
  ],
  internalLinks: [
    { name: "Emergency passport in Nashville TN", route: "/nashville" },
    { name: "Expedited passport in Knoxville TN", route: "/knoxville" },
    { name: "Urgent passport in Oak Ridge TN", route: "/oak-ridge" },
    { name: "Tennessee passport hub", route: "/tennessee" },
    { name: "Fast passport in Chattanooga TN", route: "/chattanooga" },
    { name: "Expedited passport in Johnson City TN", route: "/johnson-city" },
    { name: "Emergency passport in Maryville TN", route: "/maryville" },
  ],
  fpcOfficeAddress: "805 Broadway, Nashville, TN 37203",
  fpcOfficeNote: "Fast Passport Center serves Oak Ridge remotely via secure courier. Our registered couriers hand-deliver your documents to the Atlanta Passport Agency at 805 Broadway — approximately 180 miles west. Oak Ridge's location in Anderson County, combined with its concentration of ORNL scientists and DOE contractors who frequently travel internationally, makes our courier service essential for the Secret City's globally-connected community.",
  fpcOfficeDistance: "Serving all of Anderson County and the East Tennessee corridor",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Oak Ridge, Clinton, Norris, Lake City, and the Anderson County corridor. From Oak Ridge National Laboratory scientists heading to international research facilities to DOE contractors traveling abroad, we help the Secret City's globally-connected professionals get their passports fast.",
  nearbyCitiesIntro: "We serve Oak Ridge and nearby communities including Clinton, Norris, Lake City, Oliver Springs, Kingston, Harriman, and the entire Anderson County area.",
  howItWorksIntro: "Most Oak Ridge travelers complete everything online and ship documents overnight. At 180 miles from Nashville, Fast Passport Center provides reliable courier turnaround for Anderson County residents — eliminating the nearly 3-hour drive to the Atlanta Passport Agency.",
  mistakesIntro: "Oak Ridge travelers, including ORNL scientists traveling internationally and DOE contractors with global assignments, face unique passport challenges given the scientific and security-sensitive nature of their work. Here are the most common mistakes we see.",
  testimonialsIntro: "Oak Ridge and Anderson County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Oak Ridge? Here are the most common ones we hear from ORNL scientists and Anderson County travelers.",
  statePageRoute: "/states/tennessee",
  statePageName: "Tennessee",
  dmvWebsite: "https://www.tn.gov/safety/driver-services.html",
  dmvLabel: "Tennessee Department of Safety — Official Website",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "No drive or appointment needed",
        "Ideal for ORNL scientists heading to international conferences",
        "Perfect for DOE contractors on global assignments",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service USPS",
        "Good for planned travel from TYS or BNA",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–2",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "No document review — errors cause rejection",
        "In-person clerk appointment required",
        "Workload shifts with government shutdowns",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "Cannot upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins at 805 Broadway",
        "Life-or-death emergencies only",
        "180 miles to Nashville (2 hours 45 minutes via I-40)",
        "Still requires pre-scheduled NPIC appointment",
      ],
      highlight: false,
      emergency: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "Oak Ridge is 180 miles from the Atlanta Passport Agency — our courier saves Anderson County scientists a 6-hour round trip.",
      bullets: ["Anderson County coverage", "24–48 hour turnaround available", "No appointment or drive needed"],
      badge: "ORNL Trusted",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for Oak Ridge adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No acceptance clerk visit for most renewals", "Mail-in or courier options", "Serving Oak Ridge and Anderson County"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support in Oak Ridge and Anderson County.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Fast replacement for Oak Ridge, Clinton, and Anderson County residents with DS-64 form guidance.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement via Nashville agency"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support in Anderson County.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport name change service after marriage, divorce, or legal changes for Oak Ridge and Anderson County residents.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport Oak Ridge TN", description: "Urgent passport processing for Oak Ridge, TN travelers via Atlanta Passport Agency courier — 180 miles from Oak Ridge." },
    { name: "Passport Renewal Oak Ridge TN", description: "Fast passport renewal for Oak Ridge and Anderson County, Tennessee residents." },
    { name: "New Passport Oak Ridge TN", description: "First-time passport application assistance for Oak Ridge and East Tennessee area residents." },
    { name: "Lost Passport Oak Ridge TN", description: "Passport replacement for lost or stolen passports for Oak Ridge and Anderson County residents." },
    { name: "Child Passport Oak Ridge TN", description: "Minor passport services for Oak Ridge and Anderson County families." },
    { name: "Name Change Passport Oak Ridge TN", description: "Passport name change service for Oak Ridge, TN residents." },
  ],
  testimonials: [
    {
      name: "Dr. James C.",
      location: "ORNL Research Scientist",
      rating: 5,
      text: "I'm a physicist at Oak Ridge National Lab and was invited to present at CERN in Switzerland with 10 days notice. My passport had 3 months until expiration — Switzerland requires 6. Fast Passport Center expedited my renewal in 3 business days. The courier service handled everything while I focused on preparing my presentation.",
      service: "Emergency Renewal",
    },
    {
      name: "Sandra K.",
      location: "DOE Contractor",
      rating: 5,
      text: "Department of Energy contractor assigned to an international facility inspection in France. My security clearance paperwork was done but I realized my passport had expired 2 months prior. Fast Passport Center got me a renewal in 4 business days. The dedicated agent understood the urgency of government contracting timelines.",
      service: "Expedited Renewal",
    },
    {
      name: "The Olson Family",
      location: "Oak Ridge, TN",
      rating: 5,
      text: "ORNL engineer and teacher couple planning a summer trip to Japan. We needed two adult renewals and two child passports. Fast Passport Center handled the entire family order — all four passports arrived in 7 business days. The personal agent was fantastic at coordinating everything.",
      service: "Family Passport Package",
    },
    {
      name: "Robert L.",
      location: "Clinton, TN",
      rating: 5,
      text: "Clinton resident who won a company incentive trip to Australia — 12 days before departure. My passport was expired. Fast Passport Center got my renewal back in 5 business days. I was on that plane to Sydney thanks to their courier service to Nashville.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "or-1",
      name: "Oak Ridge Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "301 S Tulane Ave",
      city: "Oak Ridge",
      state: "TN",
      zip: "37830",
      phone: "(865) 483-5151",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Oak Ridge — Downtown",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Primary acceptance facility for Oak Ridge and Anderson County residents. Appointment required. Located near Jackson Square area.",
      icon: "ri-mail-line",
    },
    {
      id: "or-2",
      name: "Anderson County Clerk Office — Oak Ridge",
      type: "County Clerk Passport Acceptance",
      address: "728 Emory Valley Rd",
      city: "Oak Ridge",
      state: "TN",
      zip: "37830",
      phone: "(865) 483-0202",
      hours: "Mon–Fri: 8:30 AM – 5:00 PM",
      distance: "Oak Ridge — Emory Valley",
      website: "https://www.andersoncountytn.gov",
      websiteLabel: "Anderson County Government",
      notes: "County clerk passport acceptance in the Oak Ridge satellite office. Convenient alternative to the downtown post office for Anderson County residents.",
      icon: "ri-government-line",
    },
    {
      id: "or-3",
      name: "Clinton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "305 N Main St",
      city: "Clinton",
      state: "TN",
      zip: "37716",
      phone: "(865) 457-2683",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Clinton — 7 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Clinton, Norris, and northern Anderson County residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "or-4",
      name: "Farragut Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "11416 Kingston Pike",
      city: "Farragut",
      state: "TN",
      zip: "37934",
      phone: "(865) 966-0515",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Farragut — 15 miles southeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for southern Anderson County and Farragut residents. Located near the Knox County line.",
      icon: "ri-mail-line",
    },
    {
      id: "or-5",
      name: "Oliver Springs Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "515 Main St",
      city: "Oliver Springs",
      state: "TN",
      zip: "37840",
      phone: "(865) 435-6631",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Oliver Springs — 8 miles southwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Small-town option for southern Anderson County and Roane County residents. Call ahead to confirm passport hours.",
      icon: "ri-mail-line",
    },
    {
      id: "or-6",
      name: "Anderson County Clerk — Main Office",
      type: "County Clerk Passport Acceptance",
      address: "100 N Main St, Room 111",
      city: "Clinton",
      state: "TN",
      zip: "37716",
      phone: "(865) 457-6236",
      hours: "Mon–Fri: 8:00 AM – 5:00 PM",
      distance: "Clinton — 7 miles north (Courthouse)",
      website: "https://www.andersoncountytn.gov",
      websiteLabel: "Anderson County Government",
      notes: "Main Anderson County Clerk office at the historic Anderson County Courthouse in Clinton. Full passport acceptance services.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Distance to Agency", agency: "180 miles — 3 hour drive to Atlanta via I-75, but need appointment", fpc: "We drive for you — no appointment needed" },
    { feature: "Appointment Needed", agency: "Yes — call NPIC, very limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
    { feature: "Best Price Guarantee", agency: "No — government fees only", fpc: "Yes — 30-100% less than resellers" },
    { feature: "Local Expertise", agency: "Generic — no local knowledge", fpc: "Yes — ORNL & DOE scientific travel guidance" },
  ],
  mistakes: [
    {
      icon: "ri-map-pin-line",
      title: "Assuming Knoxville Has a Passport Agency — It Does Not",
      description: "Oak Ridge residents, only 25 miles from Knoxville, often assume Tennessee's third-largest city has a passport agency. It doesn't. The only Tennessee passport agency is in Nashville at 805 Broadway — 180 miles west. Driving to Knoxville for passport processing yields no special access.",
    },
    {
      icon: "ri-shield-keyhole-line",
      title: "ORNL Scientists Confusing Security Clearance With Passport Validity",
      description: "Oak Ridge National Laboratory scientists with DOE security clearances sometimes assume their clearance documentation substitutes for passport requirements. It does not. A valid passport is entirely separate from security clearance. ORNL researchers must maintain both independently for international travel.",
    },
    {
      icon: "ri-global-line",
      title: "Not Checking Visa Requirements for Scientific Conferences",
      description: "ORNL scientists heading to international research conferences often focus exclusively on the passport and forget visa requirements. Countries like China, Russia, and India require scientific conference visas that take 4–8 weeks to process. Start passport preparation 3–4 months before international conferences.",
    },
    {
      icon: "ri-restart-line",
      title: "DOE Contractors with Passports About to Expire During Long-Term Assignments",
      description: "DOE contractors on 6-month international assignments sometimes depart with a passport that expires mid-assignment. Most countries require 6 months validity beyond your stay — and renewing a U.S. passport from overseas is far more complicated. Always check your passport expiration covers your entire assignment plus 6 months.",
    },
    {
      icon: "ri-calendar-line",
      title: "Underestimating the Passport Rush Before Major Scientific Conferences",
      description: "Oak Ridge experiences passport application surges before major ORNL conference seasons — particularly spring (March–May) and fall (September–October). USPS and county clerk appointments fill quickly during these periods. Start passport applications at least 3 months before major international conferences.",
    },
    {
      icon: "ri-image-line",
      title: "Using ORNL Badge Photos for Passport Applications",
      description: "ORNL employees sometimes try to use their lab badge photos or security credential photos for passport applications. Passport photos require a specific white background, neutral expression, and exact 2x2 inch dimensions — badge photos meet none of these requirements. Always have new passport-compliant photos taken.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send a prepaid overnight label. Drop at any UPS location in Oak Ridge, Clinton, or Knoxville." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team reviews every document, then couriers to the Atlanta Passport Agency — 180 miles west via I-40." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at the Atlanta Passport Agency via our pre-secured courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Oak Ridge or Anderson County address — fully trackable." },
  ],
  faqs: [
    {
      q: "How far is Oak Ridge from the Nashville passport agency?",
      a: "Oak Ridge is approximately 180 miles from the Atlanta Passport Agency at 805 Broadway — about a 2 hour 45 minute drive west via TN-62 and I-40. While Oak Ridge is closer to Nashville than some East Tennessee cities, the round trip is still nearly 6 hours. Fast Passport Center's courier eliminates this drive entirely.",
    },
    {
      q: "Does Oak Ridge have its own passport agency?",
      a: "No. Oak Ridge does not have a passport agency. The closest passport agency is the Atlanta Passport Agency at 805 Broadway, about 180 miles west. Oak Ridge only has passport acceptance facilities — the Oak Ridge Post Office at 301 S Tulane Ave and the Anderson County Clerk's office — which can accept your DS-11 application but cannot process or expedite it.",
    },
    {
      q: "I'm an ORNL scientist — do I need special documentation for my passport application?",
      a: "ORNL scientists and researchers follow the same passport application process as any U.S. citizen. However, if you hold a security clearance, your passport application itself does not require clearance documentation. The key differences for ORNL staff are planning timelines — international scientific conferences often require both a passport and a country-specific visa, so plan 3–4 months ahead.",
    },
    {
      q: "Where do Oak Ridge travelers typically fly internationally?",
      a: "Oak Ridge travelers typically use McGhee Tyson Airport (TYS, about 30 miles south in Alcoa) for domestic connections, then connect through major hubs. Nashville International Airport (BNA, about 180 miles west) offers more direct international flights. Both airports are accessible from Oak Ridge and require a valid passport book for international flights.",
    },
    {
      q: "How fast can I get an emergency passport in Oak Ridge?",
      a: "Emergency processing is typically 24–48 hours for Oak Ridge residents. Standard expedited processing runs 2–9 business days. Given Oak Ridge's location on major transportation corridors connecting to Nashville, overnight courier routing is efficient and reliable.",
    },
    {
      q: "Where can I get passport photos in Oak Ridge?",
      a: "Walgreens at 1000 Oak Ridge Turnpike and CVS Pharmacy at 132 Main St E in Oak Ridge both offer passport photo services. UPS Store locations in Oak Ridge also provide photo services. For ORNL employees, the Oak Ridge Turnpike Walgreens is conveniently located near the main lab entrance.",
    },
    {
      q: "Can the Anderson County Clerk process my passport in Oak Ridge?",
      a: "Yes. The Anderson County Clerk's satellite office at 728 Emory Valley Rd in Oak Ridge accepts passport applications. The main Anderson County Clerk office at the Courthouse in Clinton (100 N Main St, Room 111) also offers passport acceptance. Both locations can accept DS-11 applications — call ahead to confirm current hours.",
    },
    {
      q: "How do I renew my passport in Oak Ridge?",
      a: "Adult renewals meeting all DS-82 eligibility criteria can be done entirely by mail. Fast Passport Center's courier service achieves 2–9 business day turnaround — far faster than standard USPS processing (8–11 weeks). If you're not eligible for mail-in renewal, visit the Oak Ridge Post Office or Anderson County Clerk for an in-person DS-11 application.",
    },
    {
      q: "I'm a DOE contractor assigned overseas — can I renew my passport while abroad?",
      a: "Yes, U.S. citizens can renew passports at U.S. embassies and consulates abroad. However, processing times at overseas posts vary widely and can take 4–8 weeks. If your DOE assignment is approaching and your passport is expiring, it's far better to renew through Fast Passport Center before departing the U.S.",
    },
    {
      q: "Does the Oak Ridge Post Office take Saturday passport appointments?",
      a: "No. The Oak Ridge Post Office at 301 S Tulane Ave only processes passport applications Monday through Friday from 9:00 AM to 4:00 PM. Saturday appointments are not available. Plan your visit during weekday hours and call (865) 483-5151 to schedule.",
    },
    {
      q: "Can Fast Passport Center help Clinton and Norris residents too?",
      a: "Absolutely. We serve all of Anderson County including Oak Ridge, Clinton, Norris, Lake City, Oliver Springs, and surrounding communities. Residents of neighboring towns never need to drive to Oak Ridge or Nashville — our courier service handles everything from your home or office.",
    },
    {
      q: "How does the Oak Ridge Post Office compare to the Anderson County Clerk for passport speed?",
      a: "Neither the Oak Ridge Post Office nor the Anderson County Clerk's office can expedite passport processing — both are acceptance facilities only. They accept your DS-11 application and forward it to the State Department. For actual processing speed, Fast Passport Center's registered courier service to Nashville achieves 2–9 business day turnaround — the fastest option available to Oak Ridge residents.",
    },
  ],
  photoLocations: [
    { name: "Walgreens — Oak Ridge", address: "1000 Oak Ridge Turnpike, Oak Ridge, TN 37830", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — Oak Ridge", address: "132 Main St E, Oak Ridge, TN 37830", hours: "Mon–Sun: 8AM–9PM" },
    { name: "UPS Store — Oak Ridge", address: "1230 Oak Ridge Turnpike, Ste 130, Oak Ridge, TN 37830", hours: "Mon–Fri: 8AM–6PM, Sat: 9AM–2PM" },
    { name: "Walgreens — Clinton", address: "2300 N Charles G Seivers Blvd, Clinton, TN 37716", hours: "Mon–Sun: 7AM–10PM" },
    { name: "FedEx Office — Knoxville", address: "1518 Cumberland Ave, Knoxville, TN 37916", hours: "Mon–Fri: 7AM–11PM, Sat: 9AM–9PM" },
    { name: "CVS Pharmacy — Knoxville", address: "4435 Kingston Pike, Knoxville, TN 37919", hours: "Mon–Sun: 8AM–9PM" },
  ],
  dmvLocations: [
    { name: "TN Driver Services — Oak Ridge", address: "475 Oak Ridge Turnpike, Oak Ridge, TN 37830", hours: "Mon–Fri: 8:30AM–5:00PM", note: "Closest Driver Services Center to Oak Ridge. REAL ID, driver's license, and state ID services for Anderson County residents. Conveniently located on the Oak Ridge Turnpike.", featured: true },
    { name: "TN Driver Services — Knoxville (Strawberry Plains)", address: "7320 Region Ln, Knoxville, TN 37914", hours: "Mon–Fri: 8:30AM–5:00PM", note: "Full-service Driver Services Center serving Knox County. About 25 miles from Oak Ridge.", featured: false },
    { name: "TN Driver Services — Lenoir City", address: "1000 W Broadway St, Lenoir City, TN 37771", hours: "Mon–Fri: 8:30AM–5:00PM", note: "Serves Loudon County and southern Anderson County. About 25 miles south of Oak Ridge.", featured: false },
  ],
  airports: [
    {
      code: "TYS",
      name: "McGhee Tyson Airport",
      distance: "30 miles south of Oak Ridge",
      address: "2055 Alcoa Hwy, Alcoa, TN 37701",
      note: "Closest commercial airport to Oak Ridge with daily flights to major hubs including Atlanta, Charlotte, Dallas, and Chicago. About 40 minutes via TN-162 and I-140. Primary gateway for ORNL international travelers.",
      flightUrl: "https://flyknoxville.com/",
      flightLabel: "TYS Airport",
      featured: true,
    },
    {
      code: "BNA",
      name: "Nashville International Airport",
      distance: "180 miles west of Oak Ridge",
      address: "1 Terminal Dr, Nashville, TN 37214",
      note: "Tennessee's busiest airport with extensive domestic routes and growing international service. Co-located with Atlanta Passport Agency. About 2 hours 45 minutes via I-40.",
      flightUrl: "https://flynashville.com/",
      flightLabel: "BNA Airport",
      featured: false,
    },
    {
      code: "CHA",
      name: "Chattanooga Metropolitan Airport",
      distance: "90 miles south of Oak Ridge",
      address: "1001 Airport Rd, Chattanooga, TN 37421",
      note: "Regional airport with domestic routes. About 1 hour 45 minutes via TN-58 and I-75. Alternative for Oak Ridge travelers heading south.",
      flightUrl: "https://www.chattairport.com/",
      flightLabel: "CHA Airport",
      featured: false,
    },
    {
      code: "OQT",
      name: "Oak Ridge Airport (General Aviation)",
      distance: "5 miles east of Oak Ridge",
      address: "100 Aviation Ln, Oak Ridge, TN 37830",
      note: "General aviation airport for private and charter aircraft. No commercial passenger service. ORNL travelers use TYS or BNA for commercial international departures. Convenient for private charters by DOE contractors.",
      flightUrl: "",
      flightLabel: "Oak Ridge Airport",
      featured: false,
    },
  ],
  vitalRecordsIcon: "ri-heart-pulse-line",
  vitalRecordsLinks: [
    { icon: "ri-file-text-line", label: "Birth & Death Certificates", desc: "Tennessee Department of Health Vital Records in Nashville — order certified birth and death certificates for Anderson County residents.", url: "https://www.tn.gov/health/health-program-areas/vital-records.html" },
    { icon: "ri-heart-line", label: "Marriage Records", desc: "Anderson County Clerk — certified marriage licenses for name change passport applications.", url: "https://www.andersoncountytn.gov/" },
    { icon: "ri-scales-line", label: "Divorce Decrees", desc: "Anderson County Circuit Court Clerk — certified divorce decrees supporting passport name change.", url: "https://www.tncourts.gov/" },
    { icon: "ri-global-line", label: "VitalChek — Nationwide Records", desc: "Order certified copies online through VitalChek for delivery in 5–10 business days.", url: "https://www.vitalchek.com/" },
  ],
};